import React, { useState, useRef } from "react";
import { toast } from "react-toastify";
import { startAssistant, stopAssistant } from "../../../utils/vapiClient";
import DashboardHeader from "./widgets/DashboardHeader";
import CallControls from "./widgets/CallControls";
import CallStatusBanner from "./widgets/CallStatusBanner";
import LoadingSpinner from "./widgets/LoadingSpinner";
import AccountStrip from "./widgets/AccountStrip";
import TranscriptPlayer from "./widgets/TranscriptPlayer";
import { DEPLOYMENT_CONFIG } from "../../../config/deployment";

const getRingtoneUrl = () => {
	const basePath = DEPLOYMENT_CONFIG.getCurrentPath();
	return process.env.REACT_APP_RINGTONE_URL ||
		`${basePath}/ringtone/professional-ringtone.mp3`;
};

const CandidateDashboard = ({ user }) => {
  const [callType, setCallType] = useState("Anytime Script");
  const [customerMood, setCustomerMood] = useState("Happy");
  const [gender, setGender] = useState("M");
  const [issue, setIssue] = useState("");
  const [voiceId, setVoiceId] = useState("Rohan");
  const [callState, setCallState] = useState({
    active: false,
    callId: null,
    starting: false,
    stopping: false,
  });
  const [fieldsDisabled, setFieldsDisabled] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [audioUrl, setAudioUrl] = useState("");
  const [transcriptUrl, setTranscriptUrl] = useState("");
  const [callsummary, setCallsummary] = useState("");
  const [feedbackDownload, setFeedbackDownload] = useState({});
  const ringtoneRef = useRef(null);

  function generateGUID() {
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
		const r = (Math.random() * 16) | 0;
		const v = c === "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
  }
  const [feedbackId, setFeedbackId] = useState(generateGUID());

  const getAssistantIdForCallType = (type) => {
    switch (type) {
        case "Anytime Script":
            return process.env.REACT_APP_VAPI_ASSISTANT_ID_ANYTIME_SCRIPT || "";
        case "Anytime Script - L1":
            return process.env.REACT_APP_VAPI_ASSISTANT_ID_ANYTIME_SCRIPT_L1 || "cecbb4ff-770b-47c9-83ef-e999aaad8645";
        case "Anytime Script - L2":
            return process.env.REACT_APP_VAPI_ASSISTANT_ID_ANYTIME_SCRIPT_L2 || "e469bc10-2ef6-4935-b16d-ff2be4feacb9";
        case "Anytime Script - L3":
            return process.env.REACT_APP_VAPI_ASSISTANT_ID_ANYTIME_SCRIPT_L3 || "29fca220-e33f-482e-b214-ebde1a6f7a00";
        case "Anytime Script - L4":
            return process.env.REACT_APP_VAPI_ASSISTANT_ID_ANYTIME_SCRIPT_L4 || "98cb6512-91b1-4ee3-bdf6-ed804ad90e9a";
        case "ISO":
            return process.env.REACT_APP_VAPI_ASSISTANT_ID_ISO || "";
        case "PMI Additionals":
            return process.env.REACT_APP_VAPI_ASSISTANT_ID_PMI_ADDITIONALS || "";
        case "MAINT Maintenance":
            return process.env.REACT_APP_VAPI_ASSISTANT_ID_MAINT_MAINTENANCE || "";
        default:
            return process.env.REACT_APP_VAPI_ASSISTANT_ID || "";
    }
  };

  const handleCallTypeChange = (newCallType) => {
    setCallType(newCallType);
    // Reset issue when switching away from ISO
    if (newCallType !== 'ISO') {
      setIssue('');
    }
  };

  const playRingtone = () => {
    if (ringtoneRef.current) {
      ringtoneRef.current.currentTime = 0;
      ringtoneRef.current.play();
      setTimeout(() => {}, 3000);
    }
  };
  const stopRingtone = () => {
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    }
  };

  const startVapiCall = async () => {
    try {
      setCallState((s) => ({ ...s, starting: true }));
      const newFeedbackId = generateGUID();
	  setFeedbackId(newFeedbackId);
      const assistantId = getAssistantIdForCallType(callType);
      playRingtone();
      await startAssistant(assistantId, {
        feedbackId: newFeedbackId,
        traineeId: user?.id || user?._id,
        traineeName: user?.name,
        traineeEmail: user?.email,
        callType,
        customerMood,
        issue: callType === 'ISO' ? issue : undefined,
        score: 0,
        feedback: ""
      });
      setCallState({
        active: true,
        callId: null,
        starting: false,
        stopping: false,
      });
      toast.success("Call started");
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Error starting call");
      setCallState((s) => ({ ...s, starting: false }));
      stopRingtone();
    }
  };

  const stopVapiCall = async () => {
    try {
      setCallState((s) => ({ ...s, stopping: true }));
      await stopAssistant();
      toast.info("Call stopped");
      setCallState({
        active: false,
        callId: null,
        starting: false,
        stopping: false,
      });
      setFieldsDisabled(false);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Error stopping call");
      setCallState((s) => ({ ...s, stopping: false }));
    }
  };

  // Voice list and randomizer (copied from TraineeDashboard)
  const voiceList = [
    // { name: "Cole", age: 22, gender: "M" },
	  // { name: "Harry", age: 24, gender: "M" },
	  // { name: "Spencer", age: 26, gender: "F" },
	  // { name: "Kylie", age: 23, gender: "F" },
	  // { name: "Savannah", age: 25, gender: "F" },
	  // { name: "Paige", age: 26, gender: "F" },
	  // { name: "Rohan", age: 24, gender: "M" },
	  // { name: "Elliot", age: 25, gender: "M" }
	  { name: "gYr8yTP0q4RkX1HnzQfX", age: 50, gender: "M", provider: "11labs" },
	  { name: "aTxZrSrp47xsP6Ot4Kgd", age: 50, gender: "F", provider: "11labs" }
  ];
  const getRandomVoiceId = (gender) => {
    const filtered = voiceList.filter(v => v.gender === gender);
    if (filtered.length === 0) return "Rohan";
    const idx = Math.floor(Math.random() * filtered.length);
    return filtered[idx];
  };

  // Update assistant and voiceId when gender or callType changes
  React.useEffect(() => {
    const updateVapiAssistant = async () => {
      const assistantId = getAssistantIdForCallType(callType);
      const newVoiceId = getRandomVoiceId(gender);
      setVoiceId(newVoiceId.name);
      const payload = {
        voice: {
          voiceId: newVoiceId.name,
          provider: newVoiceId.provider
        },
        server: {
          url: "https://ringringcloser.com/api/api/calls/vapi/webhook",
          timeoutSeconds: 20
        },
        serverMessages: ["end-of-call-report"]
      };
      try {
        const apiKey = process.env.REACT_APP_VAPI_PRIVATE_API_KEY || "b3914ca8-b10b-4a5f-bf67-12f688e332b3";
        await fetch(`https://api.vapi.ai/assistant/${assistantId}`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiKey}`
          },
          body: JSON.stringify(payload)
        });
      } catch (err) {
        // Optionally handle error
        console.error("Vapi API error:", err);
      }
    };
    updateVapiAssistant();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gender, callType]);

  const handleRandomize = () => {
    const moods = ["Happy", "Skeptical", "Anxious", "Frustrated", "Disappointed", "Neutral", "Difficult"];
    const randomMood = moods[Math.floor(Math.random() * moods.length)];
    const randomGender = Math.random() < 0.5 ? "M" : "F";
    setCustomerMood(randomMood);
    setGender(randomGender);
    setVoiceId(getRandomVoiceId(randomGender));
    setFieldsDisabled(true);
  };

  return (
    <div className="tdb tdb-corp">
      <DashboardHeader user={user} title="Candidate Portal" />
      <AccountStrip userInfo={user} />
      <div className="tdb-grid">
        <aside className="tdb-side" style={{ width: "100%" }}>
          <div className="card">
            <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 className="card-title">Start a Call</h2>
              <button className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, padding: '4px 12px' }} onClick={handleRandomize} title="Randomize selections">
                <span role="img" aria-label="dice">🎲</span> Random
              </button>
            </div>
            <CallControls
              callType={callType}
              customerMood={customerMood}
              gender={gender}
              issue={issue}
              callState={callState}
              onChangeCallType={handleCallTypeChange}
              onChangeCustomerMood={setCustomerMood}
              onChangeGender={setGender}
              onChangeIssue={setIssue}
              onStart={startVapiCall}
              onStop={stopVapiCall}
              fieldsDisabled={fieldsDisabled}
            />
            {callState.active && <CallStatusBanner callState={callState} />}
          </div>
        </aside>
      </div>
      <audio ref={ringtoneRef} src={getRingtoneUrl()} preload="auto" />
      {sidebarOpen &&
        TranscriptPlayer &&
        require("react-dom").createPortal(
          <TranscriptPlayer
            audioUrl={audioUrl}
            transcriptUrl={transcriptUrl}
            callsummary={callsummary}
            onClose={() => setSidebarOpen(false)}
            transcriberHref={feedbackDownload?.transcriberHref}
            transcriberTitle={feedbackDownload?.transcriberTitle}
            callRecordHref={feedbackDownload?.callRecordHref}
            callRecordTitle={feedbackDownload?.callRecordTitle}
          />,
          document.body
        )}
    </div>
  );
};

export default CandidateDashboard;
